import React, { useState } from 'react';
import { I18n } from 'react-redux-i18n';
import get from 'lodash.get';
import { Edit, Eye, EyeOff } from 'lucide-react';
// Reuse existing currency helper
import CurrencyHelper from '../../helpers/currencyHelper';
import CurrencyDisplay from '../common/CurrencyDisplay';
import { EntityHeaderActions } from '../common/EntityHeaderActions/EntityHeaderActions';
import type { EntityActionsMenuItem, EntityActionsMenuItemAction } from '../common/EntityActionsDropdown/EntityActionsDropdown';
import { StatusBadgeGroup } from '../common/StatusBadge';
import { PageEntityModel } from './types';
import { getPageEntityCurrency } from './getPageEntityCurrency';
import styles from './PageEntityHeader.module.css';

interface PageEntityHeaderProps {
  entity: PageEntityModel;
  isShowEmptyFields: boolean;
  changeVisibilityEmptyFields: () => void;
  history: any;
  translationBase: string; // e.g. fundraiser.fundraiser-view or team.view
  entityTypeOverride?: string; // optional explicit type for edit route
  hideEditButton?: boolean;
  primaryMenuItems?: EntityActionsMenuItem[]; // Action items at top of menu (e.g. Visit page, View donations)
  additionalMenuItems?: EntityActionsMenuItem[]; // Extra menu items at bottom (e.g. audit trail)
}

// currencyHelper originally imported from '../../../helpers/currencyHelper'; re-export fallback util to avoid deep relative changes

export const PageEntityHeader: React.FC<PageEntityHeaderProps> = ({
  entity,
  isShowEmptyFields,
  changeVisibilityEmptyFields,
  history,
  translationBase,
  entityTypeOverride,
  hideEditButton,
  primaryMenuItems = [],
  additionalMenuItems = []
}) => {
  const name = get(entity, 'name');
  const targetAmount = get(entity, 'targetAmount');
  const raisedAmount = get(entity, 'raisedAmount', 0) as number;
  const currency = getPageEntityCurrency(entity);
  const status = get(entity, 'status');
  const isPublic = get(entity, 'isPublic');
  const entityType = get(entity, 'type');
  const isAmbassador = get(entity, 'isAmbassador');
  const isAmbassadorApplicant = get(entity, 'isAmbassadorApplicant');
  const isSpam = get(entity, 'isSpam');

  const numericTarget = typeof targetAmount === 'number' ? targetAmount : 0;
  const numericRaised = typeof raisedAmount === 'number' ? raisedAmount : 0;
  const formattedTargetAmount = numericTarget > 0 ? CurrencyHelper.formatCurrency(numericTarget, currency || undefined) : null;
  const formattedRaisedAmount = CurrencyHelper.formatCurrency(numericRaised, currency || undefined);
  const progressPercentage = numericTarget > 0 ? Math.round((numericRaised / numericTarget) * 100) : 0;

  const isActive = status === 'live';
  const statusColor = isActive ? (isPublic ? 'var(--status-active-public)' : 'var(--status-active-private)') : 'var(--status-inactive)';
  const statusText = isActive ? (isPublic ? 'Active & Public' : 'Active & Private') : 'Inactive';

  const onEditClick = () => {
    if (!entity || !entity.id) return;
    const t = entityTypeOverride || entity.type || 'fundraiser';
    history.push(`/fundraisers/${t}/edit/${entity.id}/page-detail`);
  };

  const editAction: EntityActionsMenuItem = {
    key: 'edit',
    label: I18n.t(`${translationBase}.edit`),
    icon: <Edit aria-hidden="true" />,
    onSelect: onEditClick
  };

  const actionMenuItems: EntityActionsMenuItem[] = [
    // Primary action items (e.g. Visit page, View donations, etc.)
    ...primaryMenuItems,
    // Separator between primary items and settings (only if primary items exist)
    ...(primaryMenuItems.length > 0 ? [{ type: 'separator' as const, key: 'primary-separator' }] : []),
    // Toggle empty fields
    {
      key: 'toggle-empty-fields',
      label: isShowEmptyFields
        ? I18n.t(`${translationBase}.hide-empty`)
        : I18n.t(`${translationBase}.show-empty`),
      icon: isShowEmptyFields ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />,
      onSelect: changeVisibilityEmptyFields
    },
    // Additional items (e.g. audit trail)
    ...additionalMenuItems
  ];

  return (
    <div className="donation-header">
      <div>{I18n.t(`${translationBase}.entity-label`)}</div>
      <div className="donation-header-content">
        <div className="first-section-block">
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <h1>{name}</h1>
            <StatusBadgeGroup
              className="status-type"
              badges={[
                {
                  label: statusText,
                  colorVariant: 'custom',
                  backgroundColor: statusColor,
                  testId: 'entity-status'
                },
                entityTypeOverride === 'fundraiser' && isAmbassador && {
                  label: I18n.t(`${translationBase}.placehub-badge.approved`),
                  colorVariant: 'accent',
                  testId: 'ambassador-approved'
                },
                entityTypeOverride === 'fundraiser' && !isAmbassador && isAmbassadorApplicant && {
                  label: I18n.t(`${translationBase}.placehub-badge.applicant`),
                  colorVariant: 'accent',
                  variant: 'outlined',
                  testId: 'ambassador-applicant'
                },
                isSpam && {
                  label: I18n.t(`${translationBase}.spam-badge`),
                  colorVariant: 'error',
                  testId: 'spam-status'
                }
              ]}
            />
            <div className="fundraising-progress" style={{ marginTop: '16px', marginBottom: '16px' }}>
              <div className="progress-summary" style={{ marginBottom: '8px' }}>
                <span className="raised-amount" style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--status-active-public)' }}>
                  <CurrencyDisplay
                    amount={formattedRaisedAmount}
                    showCurrency={false}
                  />
                </span>
                {!formattedTargetAmount && (
                  <span style={{ margin: '0 8px', color: 'var(--text-muted)' }}>{I18n.t(`${translationBase}.progress.raised`)}</span>
                )}
                {formattedTargetAmount && (
                  <>
                    <span style={{ margin: '0 8px', color: 'var(--text-muted)' }}>{I18n.t(`${translationBase}.progress.raised-of`)}</span>
                    <span className="target-amount" style={{ fontSize: '18px', color: 'var(--text-muted)' }}>
                      {formattedTargetAmount} {I18n.t(`${translationBase}.progress.goal`)}
                    </span>
                    <span style={{ marginLeft: '8px', color: 'var(--text-muted)' }}>({progressPercentage}%)</span>
                  </>
                )}
              </div>
              {formattedTargetAmount && (
                <div className="progress-bar-container" style={{ width: '500px', height: '8px', backgroundColor: 'var(--progress-bar-bg)', borderRadius: '4px', overflow: 'hidden' }}>
                  <div
                    className="progress-bar"
                    style={{
                      width: `${Math.min(progressPercentage, 100)}%`,
                      height: '100%',
                      backgroundColor: 'var(--status-active-public)',
                      transition: 'width 0.3s ease'
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="second-section-block">
          {!hideEditButton && (
            <EntityHeaderActions
              className={styles.actionsGroup}
              editAction={editAction}
              menuItems={actionMenuItems}
              actionsLabel={I18n.t(`${translationBase}.actions-button`)}
            />
          )}
        </div>
      </div>
    </div>
  );
};
